import { AccountOrganisationCardProps as ItemProps } from 'components/OrganisationInfoCard/AccountOrganisationCard/AccountOrganisationCard';
import { Button, styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { Chip, TextV2 } from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { GroupType } from 'api';
import { Link as RouterLink } from 'react-router';
import { SpecialHandlingChip } from 'components/SpecialHandlingTag';

type OrganisationInfoCardListProps = { items: ItemProps[] };

const StyledSpan = styled(TextV2)(({ theme }) => ({
  fontWeight: `${theme.typography.fontWeightBold} !important`,
}));

const StyledDiv = styled('div')({});

const StyledLink = styled(RouterLink)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  display: 'inline-block',
  marginLeft: '4px',
  marginRight: '4px',
  fontWeight: theme.typography.fontWeightBold,
}));

const StyledItem = styled(Grid)(({ theme }) => ({
  minHeight: '54px',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '13px 20px',
  border: `1px solid ${theme.palette.grey[500]}`,
  borderTop: 'none',
}));

const OrganisationItem = ({
  id,
  name,
  isPrivateOrg,
  orgCount,
  parent,
  type,
  groupType = parent?.type,
  allowDemotion,
  onClick,
  sx,
}: ItemProps) => {
  const { t } = useTranslation();
  const groupTypeLabel: Record<GroupType, string> = {
    CUSTOMER: t('ui.event-management.create-group.group-type.customer'),
    PARTNER: t('ui.event-management.create-group.group-type.partner'),
  };

  const demotionLink =
    type === 'Organisation' ? (
      <Button
        onClick={() => {
          if (onClick) onClick(id);
        }}
        sx={{ padding: '0 !important', minWidth: '0' }}
        color="error"
      >
        {t('ui.event-management.my-account.organisation.revoke')}
      </Button>
    ) : (
      <StyledSpan component="span" color="textSecondary">
        {t('ui.event-management.my-account.group.demote-to-revoke')}
      </StyledSpan>
    );
  if (type === 'Organisation') {
    const isNameOnRowAbove = parent || isPrivateOrg;
    return (
      <StyledItem
        sx={{
          ...sx,
          flexDirection: 'column',
          alignItems: 'start',
        }}
        key={id}
      >
        {isNameOnRowAbove && (
          <Grid
            sx={[
              {
                alignItems: 'start',
              },
              (theme) => ({
                color: theme.palette.text.primary,
                fontWeight: theme.typography.fontWeightBold,
              }),
            ]}
          >
            {name}
          </Grid>
        )}
        <Grid
          sx={(theme) => ({
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: theme.spacing(spacingSizeMap.S),
            marginTop: isNameOnRowAbove ? theme.spacing(spacingSizeMap.S) : 0,
          })}
        >
          {isPrivateOrg && <SpecialHandlingChip />}
          <StyledDiv
            sx={(theme) => ({
              color: theme.palette.text.primary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            })}
          >
            {!isNameOnRowAbove && <StyledSpan>{name}</StyledSpan>}
            {parent &&
              t('ui.event-management.my-account.group.group-name', {
                groupName: parent.name,
                groupType: groupTypeLabel[groupType ?? 'PARTNER'],
              })}
          </StyledDiv>
          {allowDemotion ? (
            <StyledDiv
              sx={{
                ...(parent ? { marginLeft: '12px' } : {}),
                alignSelf: 'right',
              }}
            >
              {demotionLink}
            </StyledDiv>
          ) : null}
        </Grid>
      </StyledItem>
    );
  }
  return (
    <StyledItem
      key={id}
      sx={{
        ...sx,
      }}
    >
      <Grid
        sx={(theme) => ({
          fontWeight: theme.typography.fontWeightBold,
          color: theme.palette.text.primary,
        })}
      >
        {name}
        <Chip
          label={groupTypeLabel[groupType ?? 'PARTNER']}
          color={groupType === 'PARTNER' ? 'green' : 'orange'}
          sx={{ margin: '0 8px !important' }}
        />
      </Grid>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {orgCount ? (
          <StyledLink to={`/groups/${id}`}>
            {orgCount > 1
              ? t('ui.event-management.my-account.group.count.plural', {
                  count: orgCount,
                })
              : t('ui.event-management.my-account.group.count.singular', {
                  count: orgCount,
                })}
          </StyledLink>
        ) : null}
        {allowDemotion ? (
          <StyledDiv sx={{ marginLeft: '12px' }}>{demotionLink}</StyledDiv>
        ) : null}
      </Grid>
    </StyledItem>
  );
};

const OrganisationInfoCardList = ({ items }: OrganisationInfoCardListProps) => {
  return (
    <Grid
      data-testid="org-card-list"
      sx={{ padding: '0 0 2rem 0 !important' }}
      size={12}
    >
      {items.map((item, i) => {
        let sx = {};
        if (i === 0) {
          sx = {
            borderTopRightRadius: '4px',
            borderTopLeftRadius: '4px',
            borderTop: '1px solid #B3B3B3 !important',
          };
        }

        if (i === items.length - 1) {
          sx = {
            borderBottomRightRadius: '4px',
            borderBottomLeftRadius: '4px',
          };
        }
        return <OrganisationItem key={item.id} {...item} sx={sx} />;
      })}
    </Grid>
  );
};

export default OrganisationInfoCardList;
